import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";

// Draggable before/after slider — vertical divider handle, cursor drag on desktop, touch on mobile.
// `onHide` (V3.1 demo only) renders a discrete close icon at the bottom-left of
// the image; clicking it removes the whole 8-week comparison from the section
// so "Real people, real routines" expands to full width, matching V2.
const BeforeAfter = ({ ba, onHide, hideHeader = false }) => {
  const { mediaRadius } = useProduct();
  const [pos, setPos] = useState(50); // percent
  const wrapRef = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = (clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, pct)));
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    wrapRef.current?.setPointerCapture?.(e.pointerId);
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.clientX);
  };
  const endDrag = () => {
    dragging.current = false;
  };

  return (
    <div className="w-full">
      {!hideHeader && (
        <>
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-3">
                {ba.eyebrow}
              </div>
              <h3 className="text-2xl lg:text-[1.9rem] leading-[1.1] tracking-tight font-semibold text-neutral-900 max-w-md">
                {ba.heading}
              </h3>
            </div>
          </div>
          <p className="text-sm text-neutral-500 max-w-md leading-snug mb-6">{ba.copy}</p>
        </>
      )}

      <div
        ref={wrapRef}
        data-testid="before-after-slider"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
        style={{ clipPath: `inset(0 round ${mediaRadius || "20px"})` }}
        className="relative w-full aspect-[4/3] overflow-hidden select-none cursor-ew-resize editorial-frame bg-neutral-900 touch-none"
      >
        {/* After (full base layer) */}
        <img
          src={ba.after}
          alt="After"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
        {/* Before (clipped by pos%) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          <img
            src={ba.before}
            alt="Before"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(0.35) contrast(0.88) brightness(0.94)" }}
            draggable={false}
          />
        </div>

        {/* Labels */}
        <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white bg-black/45 backdrop-blur px-2.5 py-1">
          Before
        </span>
        <span className="absolute top-4 right-4 font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-900 bg-white/85 backdrop-blur px-2.5 py-1">
          After
        </span>

        {/* Divider line */}
        <div
          className="absolute top-0 bottom-0 w-[2px] bg-white pointer-events-none shadow-[0_0_12px_rgba(0,0,0,0.35)]"
          style={{ left: `${pos}%`, transform: "translateX(-1px)" }}
        />
        {/* Handle */}
        <div
          className="absolute top-1/2 w-11 h-11 rounded-full bg-white text-riversol-blue flex items-center justify-center shadow-[0_10px_24px_rgba(0,0,0,0.28)] pointer-events-none"
          style={{ left: `${pos}%`, transform: "translate(-50%, -50%)" }}
          aria-hidden="true"
        >
          <ArrowLeft size={14} strokeWidth={2.5} className="-mr-0.5" />
          <ArrowRight size={14} strokeWidth={2.5} className="-ml-0.5" />
        </div>

        {/* V3.1 demo-only: discrete close icon at the bottom-left of the image.
            Hides the whole 8-weeks comparison so the video carousel goes
            full-width, mirroring V2. Uses pointer-events-auto and stops the
            slider's pointer-down so clicking it doesn't move the divider. */}
        {onHide && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onHide}
            data-testid="hide-before-after"
            aria-label="Hide 8-week comparison"
            title="Hide 8-week comparison"
            className="absolute bottom-3 left-3 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-black/45 text-white/85 hover:bg-black/70 hover:text-white backdrop-blur transition-colors"
          >
            <X size={13} strokeWidth={2.5} />
          </button>
        )}
      </div>
      <p className="mt-3 font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400">{ba.note}</p>
    </div>
  );
};

// Video carousel with 2 full thumbs + peek of the 3rd; smooth pointer-drag (desktop swipe)
// and smooth arrow-triggered scroll. Infinite loop by wrapping to start/end.
const VideoCarousel = ({ videos, onPlay, separator = "/", intro, hideCounters = false }) => {
  const { mediaRadius } = useProduct();
  const n = videos.length;
  const trackRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });
  const [current, setCurrent] = useState(0);

  const stepPx = () => {
    const el = trackRef.current;
    if (!el) return 0;
    const card = el.querySelector("[data-vcard]");
    return card ? card.getBoundingClientRect().width + 16 /* gap */ : el.clientWidth * 0.4;
  };

  const updateCurrent = () => {
    const el = trackRef.current;
    if (!el) return;
    const s = stepPx();
    if (!s) return;
    setCurrent(Math.round(el.scrollLeft / s) % n);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateCurrent, { passive: true });
    return () => el.removeEventListener("scroll", updateCurrent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollByCards = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const s = stepPx();
    const max = el.scrollWidth - el.clientWidth;
    // Infinite wrap
    if (dir > 0 && el.scrollLeft >= max - 4) {
      el.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    if (dir < 0 && el.scrollLeft <= 4) {
      el.scrollTo({ left: max, behavior: "smooth" });
      return;
    }
    el.scrollBy({ left: dir * s, behavior: "smooth" });
  };

  const onPointerDown = (e) => {
    const el = trackRef.current;
    if (!el) return;
    // Only left mouse button / primary pointer initiates a potential drag.
    if (e.pointerType === "mouse" && e.button !== 0) return;
    drag.current = { down: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false, captured: false, pointerId: e.pointerId };
  };
  const onPointerMove = (e) => {
    if (!drag.current.down) return;
    const el = trackRef.current;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 6) {
      // Only start capturing/scrolling once we clearly detected a drag — small clicks reach buttons.
      if (!drag.current.captured) {
        drag.current.captured = true;
        drag.current.moved = true;
        el.setPointerCapture?.(drag.current.pointerId);
        el.classList.add("cursor-grabbing");
      }
      el.scrollLeft = drag.current.startScroll - dx;
    }
  };
  const endDrag = (e) => {
    const el = trackRef.current;
    if (drag.current.captured) {
      try { el?.releasePointerCapture?.(drag.current.pointerId); } catch {}
      el?.classList.remove("cursor-grabbing");
    }
    drag.current.down = false;
  };
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-3">
            Real Routines
          </div>
          <h3 className="text-2xl lg:text-[1.9rem] leading-[1.1] tracking-tight font-semibold text-neutral-900">
            Real people, real routines
          </h3>
        </div>
      </div>
      <p className="text-sm text-neutral-500 max-w-sm leading-snug mb-6" data-testid="ugc-intro">
        {intro || <>Tap any clip to play — swipe or use the arrows to loop through all {String(n).padStart(2, "0")}.</>}
      </p>

      <div
        ref={trackRef}
        className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory cursor-grab select-none hide-scrollbar pb-2"
        style={{ scrollbarWidth: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={onClickCapture}
      >
        {videos.map((v, i) => (
          <button
            key={v.id}
            data-vcard
            onClick={() => onPlay(v)}
            data-testid={`ugc-video-${i}`}
            aria-label="Play video"
            style={{ clipPath: `inset(0 round ${mediaRadius || "15px"})` }}
            className="group relative snap-start shrink-0 basis-[calc((100%-16px-16px)/2.4)] aspect-[9/16] editorial-frame bg-riversol-stone block"
          >
            <img src={v.thumb} alt="" draggable={false} className="w-full h-full object-cover img-zoom" loading="lazy" />
            <span className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex items-center justify-center w-14 h-14 rounded-full border border-white/70 bg-white/10 backdrop-blur-md text-white opacity-90 transition-all duration-300 group-hover:scale-110 group-hover:bg-white group-hover:text-riversol-blue group-hover:shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
                <Play size={16} className="fill-current ml-0.5" />
              </span>
            </span>
            {!hideCounters && (
              <span className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white/90">
                {String(i + 1).padStart(2, "0")}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mt-6">
        <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-500">
          {!hideCounters && <>{String(current + 1).padStart(2, "0")} {separator} {String(n).padStart(2, "0")}</>}
        </span>
        <div className="flex items-center gap-3">
          <button
            onClick={() => scrollByCards(-1)}
            data-testid="ugc-carousel-prev"
            aria-label="Previous clip"
            className="w-11 h-11 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-900 bg-white hover:bg-riversol-blue hover:border-riversol-blue hover:text-white transition-all"
          >
            <ArrowLeft size={16} />
          </button>
          <button
            onClick={() => scrollByCards(1)}
            data-testid="ugc-carousel-next"
            aria-label="Next clip"
            className="w-11 h-11 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-900 bg-white hover:bg-riversol-blue hover:border-riversol-blue hover:text-white transition-all"
          >
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export const RiversolInAction = () => {
  const P = useProduct();
  const { ugc, beforeAfter } = P;
  const [active, setActive] = useState(null);
  // V3.1 demo-only: hide the before/after panel so the video carousel fills
  // the row (matches V2). Not persisted — a page refresh brings it back so
  // the client can re-run the demo cleanly.
  const [baHiddenV31, setBaHiddenV31] = useState(false);
  const hideBa = () => setBaHiddenV31(true);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!ugc) return null;

  const split = !!beforeAfter && !(P.v31 && baHiddenV31) && !P.v34;
  const baSection = P.v34 && beforeAfter ? P.beforeAfterSection || {} : null;

  return (
    <>
    {baSection && (
      <section className="bg-white py-20 lg:py-24" data-testid="before-after-section" data-keep-padding>
        <div className="max-w-[1600px] mx-auto px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 min-w-0">
              <Reveal>
                <BeforeAfter ba={beforeAfter} hideHeader />
              </Reveal>
            </div>
            <div className="lg:col-span-5 min-w-0 lg:pl-4">
              <Reveal delay={0.1}>
                <h2 className="text-4xl lg:text-[3rem] leading-[1.05] tracking-tight font-semibold text-neutral-900" data-testid="before-after-heading">
                  {baSection.heading || "See the difference."}
                </h2>
                {baSection.body && (
                  <p className="mt-6 text-lg leading-relaxed text-neutral-600 max-w-md" data-testid="before-after-body">
                    {baSection.body}
                  </p>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    )}
    <section className="bg-riversol-paper py-28 border-y border-neutral-200" data-testid="riversol-in-action" data-keep-padding>
      <div className="max-w-[1600px] mx-auto px-8">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-6">
              {split ? "Real results & real routines" : "Real routines"}
            </div>
            <h2 className="text-4xl lg:text-[3rem] leading-[1.05] tracking-tight font-semibold text-neutral-900">
              {ugc.heading}
            </h2>
            {!split && ugc.intro && (
              <p className="mt-4 text-base text-neutral-500 max-w-md leading-snug" data-testid="ugc-intro">{ugc.intro}</p>
            )}
          </div>
          {/*
          <span className="font-mono text-[11px] tracking-[0.24em] uppercase text-neutral-400 hidden md:block">
            {String(ugc.videos.length).padStart(2, "0")} clips
          </span>*/}
        </div>

        {split ? (
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14 items-start">
            <div className="lg:col-span-7 min-w-0">
              <Reveal>
                <BeforeAfter ba={beforeAfter} onHide={P.v31 ? hideBa : undefined} />
              </Reveal>
            </div>
            <div className="lg:col-span-5 min-w-0">
              <Reveal delay={0.1}>
                <VideoCarousel videos={ugc.videos} onPlay={setActive} separator={P.v31 ? "·" : "/"} intro={ugc.intro} hideCounters={P.v34} />
              </Reveal>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile / tablet: reuse V3's swipeable video carousel to keep parity */}
            <div className="lg:hidden">
              <VideoCarousel videos={ugc.videos} onPlay={setActive} separator={P.v31 ? "·" : "/"} intro={ugc.intro} hideCounters={P.v34} />
            </div>

            {/* Desktop: original 5-column editorial grid */}
            <div className="hidden lg:grid grid-cols-5 gap-5">
              {ugc.videos.map((v, i) => (
                <Reveal key={v.id} delay={i * 0.08}>
                  <button
                    onClick={() => setActive(v)}
                    data-testid={`ugc-video-${i}`}
                    aria-label="Play video"
                    style={{ clipPath: `inset(0 round ${P.mediaRadius || "15px"})` }}
                    className="group relative w-full aspect-[9/16] editorial-frame bg-riversol-stone block"
                  >
                    <img src={v.thumb} alt="" className="w-full h-full object-cover img-zoom" loading="lazy" />
                    <span className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex items-center justify-center w-16 h-16 rounded-full border border-white/70 bg-white/10 backdrop-blur-md text-white opacity-85 transition-all duration-300 group-hover:opacity-100 group-hover:scale-110 group-hover:bg-white group-hover:border-white group-hover:text-riversol-blue group-hover:shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
                        <Play size={18} className="fill-current ml-0.5" />
                      </span>
                    </span>
                    {!P.v34 && (
                      <span className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white/90">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    )}
                  </button>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
            data-testid="ugc-modal"
          >
            <motion.div
              className="relative"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActive(null)}
                aria-label="Close"
                data-testid="ugc-modal-close"
                className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase"
              >
                Close <X size={18} />
              </button>
              <video
                key={active.id}
                src={active.video}
                controls
                autoPlay
                playsInline
                className="h-[80vh] max-h-[900px] w-auto max-w-[92vw] rounded-sm bg-black shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
                style={{ aspectRatio: "9 / 16" }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
    </>
  );
};

export default RiversolInAction;
